import React, { useState, useEffect, useRef } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Search, Printer } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import ServiceReportView from './ServiceReportView';

const ServiceLifecycleFSR = ({ axiosInstance, currentUser }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const reportRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Adjusted to fit without scrolling

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/field-service-reports');
        // Sort reports by date descending (newest first)
        const sortedReports = response.data.sort((a, b) => {
          // Assuming date is in a format that can be parsed, 
          // but we might need to handle it more carefully if it's just a string like "2026-03-03"
          return new Date(b.date) - new Date(a.date) || b.fsr_number.localeCompare(a.fsr_number);
        });
        setReports(sortedReports);
        setError(null);
      } catch (err) {
        console.error('Error fetching FSRs:', err);
        setError('Failed to load Field Service Reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [axiosInstance]);

  // Filter reports
  const filteredReports = reports.filter(report => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (report.fsr_number?.toLowerCase() || "").includes(searchLower) ||
      (report.account_name?.toLowerCase() || "").includes(searchLower) ||
      (report.email?.toLowerCase() || "").includes(searchLower) ||
      (report.model?.toLowerCase() || "").includes(searchLower) ||
      (report.status?.toLowerCase() || "").includes(searchLower)
    );
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrint = () => {
    const printContent = reportRef.current;
    if (!printContent) return;

    const originalContents = document.body.innerHTML;
    const printContents = printContent.innerHTML;

    // Create a temporary container for printing
    const printContainer = document.createElement('div');
    printContainer.innerHTML = printContents;
    printContainer.style.width = '100%';
    printContainer.style.height = '100%';
    
    // Create a new window for printing to avoid messing with the current page state
    const printWindow = window.open('', '_blank');
    
    // Copy all styles from the main document to the new window
    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('');
        } catch (e) {
          console.log('Access to stylesheet denied', e);
          return '';
        }
      })
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Report</title>
          <style>
            ${styles}
            body { margin: 0; padding: 0; background: white; }
            @media print {
              @page { size: A4 portrait; margin: 5mm; }
              body { -webkit-print-color-adjust: exact; }
              /* Override container styles for print */
              .p-6 { padding: 0 !important; }
              .overflow-auto { overflow: visible !important; }
              .min-h-full { min-height: 0 !important; }
              .max-w-\\[210mm\\] { 
                max-width: 100% !important; 
                width: 100% !important; 
                margin: 0 auto !important;
              }
            }
          </style>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body>
          <div id="print-content-wrapper">
            ${printContents}
          </div>
          <script>
            window.onload = function() {
              const wrapper = document.getElementById('print-content-wrapper');
              // Find the inner content div (the one with max-w-[210mm] in the original)
              // We use a more generic selector or traversal to avoid escaping issues
              let content = wrapper.querySelector('div[class*="max-w-"]');
              
              if (!content && wrapper.firstElementChild) {
                // Fallback to traversing: wrapper -> p-6 div -> content div
                if (wrapper.firstElementChild.firstElementChild) {
                    content = wrapper.firstElementChild.firstElementChild;
                } else {
                    content = wrapper.firstElementChild;
                }
              }
              
              if (content) {
                const A4_HEIGHT_PX = 1123; // Approx A4 height at 96dpi
                const MAX_HEIGHT = 1080; // Safety margin (approx 1123 - 40px)
                
                const contentHeight = content.scrollHeight;
                
                if (contentHeight > MAX_HEIGHT) {
                  const scale = MAX_HEIGHT / contentHeight;
                  // Apply scaling to fit in one page
                  document.body.style.transform = 'scale(' + scale + ')';
                  document.body.style.transformOrigin = 'top center';
                  document.body.style.width = (100 / scale) + '%';
                  document.body.style.height = (contentHeight * scale) + 'px';
                  document.body.style.overflow = 'hidden';
                }
              }
              
              setTimeout(() => {
                window.print();
                window.close();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
  };

  return (
    <div className="flex h-[calc(100vh-230px)] overflow-hidden gap-4">
      {/* Left Side - 30% Width - List of Reports */}
      <div
        className="w-[30%] flex flex-col overflow-hidden rounded-2xl"
        style={{
          background: 'var(--nad-s1)',
          border: '1px solid var(--nad-b1)',
        }}
      >
        <div
          className="p-4 border-b space-y-3"
          style={{
            borderColor: 'var(--nad-b1)',
            background: 'linear-gradient(to bottom, rgba(59,130,246,0.08), rgba(250,204,21,0.04))',
          }}
        >
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--nad-t1)' }}>Field Service Reports</h2>
            <p className="text-sm" style={{ color: 'var(--nad-t3)' }}>Total: {filteredReports.length}</p>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4" style={{ color: 'var(--nad-t4)' }} />
            <Input
              placeholder="Filter reports..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-8"
              style={{
                background: 'var(--nad-s2)',
                borderColor: 'var(--nad-b1)',
                color: 'var(--nad-t1)',
              }}
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: 'transparent', borderBottomColor: 'var(--nad-accent-txt)' }}
              ></div>
            </div>
          ) : error ? (
            <div className="p-4 text-center" style={{ color: 'var(--nad-neg)' }}>{error}</div>
          ) : reports.length === 0 ? (
            <div className="p-4 text-center" style={{ color: 'var(--nad-t3)' }}>No reports found</div>
          ) : (
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ background: 'var(--nad-s2)' }}>
                      <TableHead className="w-[100px]" style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Report #</TableHead>
                      <TableHead style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Customer</TableHead>
                      <TableHead style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Email</TableHead>
                      <TableHead style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Model</TableHead>
                      <TableHead style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentItems.map((report) => (
                      <TableRow 
                        key={report.id} 
                        className="cursor-pointer"
                        style={{
                          background:
                            selectedReport?.id === report.id
                              ? 'linear-gradient(to right, rgba(250,204,21,0.16), rgba(59,130,246,0.10))'
                              : 'transparent',
                          borderColor: 'var(--nad-b1)',
                        }}
                        onClick={() => setSelectedReport(report)}
                        onMouseEnter={(e) => {
                          if (selectedReport?.id !== report.id) {
                            e.currentTarget.style.background = 'var(--nad-s2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedReport?.id !== report.id) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <TableCell className="font-medium text-xs py-2" style={{ color: 'var(--nad-t1)', borderColor: 'var(--nad-b1)' }}>{report.fsr_number}</TableCell>
                        <TableCell className="text-xs py-2" style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{report.account_name}</TableCell>
                        <TableCell className="text-xs py-2" style={{ color: 'var(--nad-t3)', borderColor: 'var(--nad-b1)' }}>{report.email || '-'}</TableCell>
                        <TableCell className="text-xs py-2" style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{report.model}</TableCell>
                        <TableCell className="py-2" style={{ borderColor: 'var(--nad-b1)' }}>
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={
                              report.status === 'Pending'
                                ? {
                                    background: 'rgba(250,204,21,0.18)',
                                    color: 'var(--nad-accent-txt)',
                                    border: '1px solid rgba(250,204,21,0.30)',
                                  }
                                : {
                                    background: 'rgba(34,197,94,0.16)',
                                    color: 'rgb(74,222,128)',
                                    border: '1px solid rgba(34,197,94,0.30)',
                                  }
                            }
                          >
                            {report.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination controls at the bottom of the left panel */}
              <div
                className="p-4 border-t mt-auto"
                style={{
                  borderColor: 'var(--nad-b1)',
                  background: 'var(--nad-s2)',
                }}
              >
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        href="#" 
                        onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink 
                          href="#" 
                          isActive={currentPage === page}
                          onClick={(e) => { e.preventDefault(); handlePageChange(page); }}
                          className="cursor-pointer"
                          style={{
                            color: currentPage === page ? 'var(--nad-accent-txt)' : 'var(--nad-t2)',
                            background: currentPage === page
                              ? 'linear-gradient(to right, rgba(250,204,21,0.18), rgba(59,130,246,0.12))'
                              : 'transparent',
                            borderColor: currentPage === page ? 'var(--nad-b2)' : 'var(--nad-b1)',
                          }}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                    <PaginationItem>
                      <PaginationNext 
                        href="#" 
                        onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Side - 70% Width - Details - Independent Scrolling */}
      <div
        className="w-[70%] flex flex-col overflow-hidden rounded-2xl"
        style={{
          background: 'var(--nad-s1)',
          border: '1px solid var(--nad-b1)',
        }}
      >
        {selectedReport && (
          <div
            className="flex items-center justify-between p-4 border-b"
            style={{
              borderColor: 'var(--nad-b1)',
              background: 'linear-gradient(to right, rgba(59,130,246,0.08), rgba(250,204,21,0.04))',
            }}
          >
            <div className="flex items-center gap-2">
               <h3 className="text-lg font-semibold" style={{ color: 'var(--nad-t1)' }}>Report Details</h3>
               <span
                 className="text-sm px-2 py-1 rounded"
                 style={{
                   color: 'var(--nad-accent-txt)',
                   background: 'rgba(250,204,21,0.12)',
                   border: '1px solid rgba(250,204,21,0.22)',
                 }}
               >
                 {selectedReport.fsr_number}
               </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                style={{
                  background: 'rgba(59,130,246,0.10)',
                  borderColor: 'rgba(59,130,246,0.30)',
                  color: 'var(--nad-accent-txt)',
                }}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        )}
        <div
          className="flex-1 overflow-y-auto p-4"
          style={{ background: 'var(--nad-s2)' }}
        >
          {selectedReport ? (
            <div ref={reportRef} className="h-full">
              <ServiceReportView report={selectedReport} />
            </div>
          ) : (
            <div className="flex-1 h-full flex flex-col items-center justify-center p-6" style={{ color: 'var(--nad-t4)' }}>
              <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg" style={{ color: 'var(--nad-t3)' }}>Select a report from the list to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceLifecycleFSR;
